import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Crown, Star, Gem } from 'lucide-react';
import Header from '../components/Header';
import { BRAND } from '../data/brand';

const TIERS = [
  {
    id: 'bronze',
    name: 'Bronze',
    icon: Star,
    price: 'Free',
    color: 'from-amber-900/20 to-transparent',
    borderColor: 'border-amber-800/30',
    accentColor: 'text-amber-600',
    description: 'Your entry into the world of Secret 13.',
    perks: [
      'Access to the full collection',
      'Member-only pricing',
      'Discreet packaging on all orders',
      'Priority customer concierge',
    ],
  },
  {
    id: 'gold',
    name: 'Gold',
    icon: Crown,
    price: '$49/mo',
    color: 'from-yellow-700/20 to-transparent',
    borderColor: 'border-yellow-600/30',
    accentColor: 'text-yellow-500',
    popular: true,
    description: 'For those who want first access and deeper rewards.',
    perks: [
      'Everything in Bronze',
      '48-hour early access to new drops',
      '10% off every order',
      'Exclusive Gold-only Secrets',
      'Free shipping on all orders',
      'Birthday Secret gift',
    ],
  },
  {
    id: 'black',
    name: 'Black',
    icon: Gem,
    price: '$149/mo',
    color: 'from-zinc-600/20 to-transparent',
    borderColor: 'border-zinc-500/30',
    accentColor: 'text-zinc-300',
    description: 'The highest tier. By invitation or commitment.',
    perks: [
      'Everything in Gold',
      'First access to limited releases',
      '20% off every order',
      'Complimentary monthly Secret',
      'Private concierge line',
      'Exclusive events & tastings',
      'Custom blending consultations',
      'Lifetime member pricing lock',
    ],
  },
];

const MembershipPage = () => {
  const navigate = useNavigate();
  const [selectedTier, setSelectedTier] = useState('gold');

  return (
    <div className="min-h-screen bg-[#050505]">
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-20 px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto text-center">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="font-sans text-xs tracking-[0.3em] uppercase text-zinc-500 mb-4"
          >
            Membership
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-zinc-50 mb-6"
            data-testid="membership-heading"
          >
            Choose Your Circle
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="font-sans text-sm text-zinc-500 max-w-lg mx-auto leading-relaxed"
          >
            Three tiers. One standard. The deeper you go, the more exclusive it gets.
          </motion.p>
        </div>
      </section>

      {/* Tiers */}
      <section className="py-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {TIERS.map((tier, index) => {
            const Icon = tier.icon;
            return (
              <motion.div
                key={tier.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.15 }}
                data-testid={`tier-${tier.id}`}
                onClick={() => setSelectedTier(tier.id)}
                className={`relative cursor-pointer border ${
                  selectedTier === tier.id ? 'border-white/20' : 'border-white/5'
                } bg-gradient-to-b ${tier.color} p-8 transition-all duration-500 hover:border-white/15`}
              >
                {tier.popular && (
                  <div className="absolute top-0 right-0 bg-white text-black px-4 py-1 font-sans text-[10px] tracking-[0.2em] uppercase">
                    Most Popular
                  </div>
                )}
                <Icon className={`w-6 h-6 ${tier.accentColor} mb-6`} />
                <h3 className="font-serif text-2xl text-zinc-50 mb-1">{tier.name}</h3>
                <p className={`font-sans text-lg font-medium ${tier.accentColor} mb-4`}>{tier.price}</p>
                <p className="font-sans text-xs text-zinc-500 mb-8 leading-relaxed">{tier.description}</p>
                <ul className="space-y-3">
                  {tier.perks.map((perk) => (
                    <li key={perk} className="font-sans text-xs text-zinc-400 flex items-start gap-2">
                      <span className={`${tier.accentColor} mt-0.5`}>-</span>
                      {perk}
                    </li>
                  ))}
                </ul>
                <button
                  data-testid={`select-${tier.id}`}
                  className={`mt-8 w-full py-3 font-sans text-xs tracking-[0.2em] uppercase transition-all duration-300 ${
                    selectedTier === tier.id
                      ? 'bg-white text-black hover:bg-zinc-200'
                      : 'border border-white/10 text-zinc-400 hover:border-white/30 hover:text-white'
                  }`}
                >
                  {tier.price === 'Free' ? 'Current Plan' : 'Select Plan'}
                </button>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">
          <p className="font-serif text-xl text-zinc-50">Secret 13</p>
          <p className="font-sans text-xs text-zinc-600">{BRAND.legal.copyright} {BRAND.legal.membersOnly}</p>
        </div>
      </footer>
    </div>
  );
};

export default MembershipPage;
